import * as React from "react";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import { FormHelperText } from "@mui/material";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface Option {
  label: string;
  value: string;
}

interface MultipleSelectWithEventsProps {
  options: Option[];
  label: string;
  error?: boolean;
  helperText?: string;
  name?: string;
  defaultSelectedValues?: any; // New prop for default selected values
  onBlur?: (e: any) => void;
  onItemsAdded?: (items: string[]) => void;
  onItemsRemoved?: (items: string[]) => void;
  onSelectionChanged?: (selectedValues: string[]) => void;
}

const MultipleSelectWithEvents: React.FC<MultipleSelectWithEventsProps> = ({
  options,
  label,
  name = "",
  defaultSelectedValues = [], // Initialize with an empty array if not provided
  error = false,
  helperText,
  onBlur = (e) => {},
  onItemsAdded,
  onItemsRemoved,
  onSelectionChanged,
}) => {
  const [selectedValues, setSelectedValues] = React.useState<string[]>(
    defaultSelectedValues
  );
  // Reset selectedValues if the options change or defaultSelectedValues changes
  React.useEffect(() => {
    // Reset selectedValues to defaultSelectedValues if options change
    setSelectedValues(defaultSelectedValues);
  }, [options, defaultSelectedValues]);

  const handleChange = (event: SelectChangeEvent<typeof selectedValues>) => {
    const {
      target: { value },
    } = event;
    const newSelectedValues =
      typeof value === "string" ? value.split(",") : value;

    // Determine added and removed items
    const addedItems = newSelectedValues.filter(
      (item) => !selectedValues.includes(item)
    );
    const removedItems = selectedValues.filter(
      (item) => !newSelectedValues.includes(item)
    );

    // Call event handlers
    if (addedItems.length > 0 && onItemsAdded) {
      onItemsAdded(addedItems);
    }
    if (removedItems.length > 0 && onItemsRemoved) {
      onItemsRemoved(removedItems);
    }

    setSelectedValues(newSelectedValues);

    if (onSelectionChanged) {
      onSelectionChanged(newSelectedValues);
    }
  };

  return (
    <FormControl
      size={"small"}
      sx={{ width: "100%", minWidth: 120 }}
      error={error}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        name={name}
        value={selectedValues}
        onChange={handleChange}
        onBlur={onBlur}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) =>
          selected
            .map(
              (value) => options.find((option) => option.value === value)?.label
            )
            .join(", ")
        }
        MenuProps={MenuProps}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={selectedValues.includes(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default MultipleSelectWithEvents;
